import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import QRCode from 'qrcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 8000;

// Color helpers — gradient from red (low %) to blue (high %)
const pctColor = (p) => {
  if (p == null) return '#6b7280';
  if (p >= 90) return '#3b82f6';        // blue
  if (p >= 80) return '#22d3ee';        // cyan
  if (p >= 70) return '#a3e635';        // lime
  if (p >= 50) return '#f59e0b';        // amber
  return '#ef4444';                     // red
};

const fmtTime = (iso) => {
  if (!iso) return '--:--';
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return '--:--'; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '--';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '--'; }
};

// Extract subject from W IA message ("Tarciso, é um Cohiba Behike..." → "Cohiba Behike")
const extractSubject = (msg) => {
  if (!msg) return 'Produto verificado';
  const m = msg.replace(/\s+/g, ' ').trim();
  // Try to capture text between "é um(a)" / "trata-se de" and the next period or comma
  const re = /(?:é\s+um[a]?|é\s+o\s+|trata[-\s]se\s+de|aqui\s+s[ãa]o|s[ãa]o\s+)\s*([^.\n]{3,80})/i;
  const match = m.match(re);
  if (match && match[1]) {
    return match[1].split(/[.,;(]/)[0].trim().slice(0, 70);
  }
  // Fallback: first ~60 chars after the comma
  const after = m.split(',').slice(1).join(',').trim();
  return (after || m).slice(0, 60);
};

const WIALive = () => {
  const [data, setData] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [selected, setSelected] = useState(null);
  const prevTopRef = useRef(null);
  const audioRef = useRef(null);

  const fetchFeed = async () => {
    try {
      const token = localStorage.getItem('wclub_admin_token');
      const res = await axios.get(`${API}/w-verify/feed?limit=200`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const next = res.data || { items: [], stats: {} };
      // Detect a new entry to trigger pulse animation
      const topKey = next.items[0] ? `${next.items[0].short_hash}-${next.items[0].sequence}` : null;
      if (prevTopRef.current && topKey && prevTopRef.current !== topKey) {
        setHighlightId(topKey);
        // Soft beep
        try {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.volume = 0.25;
            audioRef.current.play().catch(() => {});
          }
        } catch {}
        setTimeout(() => setHighlightId(null), 4000);
      }
      prevTopRef.current = topKey;
      setData(next);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Falha ao carregar feed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = data.items || [];
  const stats = data.stats || {};
  const recent = useMemo(() => items.slice(0, 6), [items]);
  const tickerItems = useMemo(() => items.slice(0, 20), [items]);
  const [activeStat, setActiveStat] = useState(null); // 'total' | 'today' | 'avg' | 'members'

  return (
    <div className="min-h-screen text-white" data-testid="wia-live-section">
      {/* Beep tone (very small base64 wav) */}
      <audio ref={audioRef} preload="auto" src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=" />

      {/* Bloomberg-style live ticker */}
      <div
        className="relative overflow-hidden border-b"
        style={{
          background: 'linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(15,20,30,0.85) 100%)',
          borderColor: 'rgba(59,130,246,0.25)',
          marginTop: 'max(calc(env(safe-area-inset-top, 0px) + 44px), 52px)',
        }}
        data-testid="wia-live-ticker"
      >
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="flex items-center gap-2 shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-[10px] font-bold tracking-[0.2em] text-red-400">W IA LIVE</span>
            <span className="text-[10px] text-white/40 font-mono">•</span>
          </div>
          <div className="relative flex-1 overflow-hidden">
            <motion.div
              className="flex gap-8 whitespace-nowrap"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: Math.max(18, tickerItems.length * 2.5), ease: 'linear', repeat: Infinity }}
            >
              {[...tickerItems, ...tickerItems].map((it, idx) => (
                <span
                  key={`${it.short_hash}-${idx}`}
                  className="text-[11px] font-mono flex items-center gap-2"
                >
                  <span className="text-white/40">#{String(it.sequence ?? '—').padStart(4, '0')}</span>
                  <span className="text-white/80 font-semibold">{(it.member_name || '—').toUpperCase()}</span>
                  <span className="text-white/50 truncate max-w-[260px]">{extractSubject(it.message)}</span>
                  <span className="font-bold" style={{ color: pctColor(it.percentage) }}>
                    {it.percentage ?? '—'}%
                  </span>
                  <span className="text-white/30">{fmtTime(it.created_at || it.date)}</span>
                  <span className="text-white/15">|</span>
                </span>
              ))}
              {tickerItems.length === 0 && (
                <span className="text-[11px] font-mono text-white/40">Aguardando autenticações…</span>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="container mx-auto px-4 pt-4 sm:pt-6 pb-3 pl-14">
        <div className="flex items-start sm:items-end justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-4xl font-['Jost'] font-light tracking-wide leading-tight">
              W IA <span className="font-bold bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg,#ef4444 0%,#f59e0b 30%,#22d3ee 70%,#3b82f6 100%)' }}>Live</span>
            </h1>
            <p className="text-[10px] sm:text-xs text-white/40 mt-1 tracking-wider uppercase">Painel de autenticações em tempo real</p>
          </div>
          <button
            onClick={fetchFeed}
            disabled={loading}
            className="shrink-0 text-[10px] sm:text-[11px] uppercase tracking-widest text-white/60 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition"
            data-testid="wia-live-refresh"
          >
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        </div>
        {error && (
          <div className="mt-3 text-xs text-red-400 border border-red-500/30 rounded-lg px-3 py-2 bg-red-500/5">
            {error}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wia-live-stats">
        <StatCard
          label="Total Autenticações"
          value={stats.total ?? 0}
          accent="#3b82f6"
          active={activeStat === 'total'}
          onClick={() => setActiveStat('total')}
          testId="wia-live-stat-total"
        />
        <StatCard
          label="Hoje"
          value={stats.today ?? 0}
          accent="#22d3ee"
          active={activeStat === 'today'}
          onClick={() => setActiveStat('today')}
          testId="wia-live-stat-today"
        />
        <StatCard
          label="Média Hoje"
          value={stats.avg_today ? `${stats.avg_today}%` : '—'}
          accent={pctColor(stats.avg_today)}
          active={activeStat === 'avg'}
          onClick={() => setActiveStat('avg')}
          testId="wia-live-stat-avg"
        />
        <StatCard
          label="Sócios Hoje"
          value={stats.distinct_members_today ?? 0}
          accent="#a3e635"
          active={activeStat === 'members'}
          onClick={() => setActiveStat('members')}
          testId="wia-live-stat-members"
        />
      </div>

      {/* Live cards feed */}
      <div className="container mx-auto px-4 mt-6">
        <SectionTitle>Últimas autenticações</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="wia-live-feed">
          <AnimatePresence initial={false} mode="popLayout">
            {recent.map((it) => {
              const key = `${it.short_hash}-${it.sequence}`;
              const isNew = highlightId === key;
              return (
                <motion.div
                  key={key}
                  layout
                  initial={{ opacity: 0, y: 24, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -16, scale: 0.96 }}
                  transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="relative rounded-2xl overflow-hidden p-4"
                  style={{
                    background: 'linear-gradient(135deg, rgba(20,25,35,0.85) 0%, rgba(8,11,18,0.95) 100%)',
                    border: `1px solid ${isNew ? pctColor(it.percentage) : 'rgba(255,255,255,0.08)'}`,
                    boxShadow: isNew
                      ? `0 0 0 1px ${pctColor(it.percentage)}55, 0 0 32px ${pctColor(it.percentage)}33`
                      : '0 2px 12px rgba(0,0,0,0.4)',
                  }}
                  data-testid="wia-live-card"
                >
                  {isNew && (
                    <motion.div
                      className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1.4, repeat: 2 }}
                      style={{ background: 'rgba(239,68,68,0.18)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.4)' }}
                    >
                      NOVO
                    </motion.div>
                  )}

                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] tracking-widest text-white/40 font-mono">
                        #{String(it.sequence ?? '—').padStart(4, '0')} • {(it.channel || 'web').toUpperCase()}
                      </div>
                      <div className="text-base font-semibold truncate mt-0.5">{it.member_name || '—'}</div>
                      <div className="text-[11px] text-white/50 mt-0.5">{fmtDateTime(it.created_at || it.date)}</div>
                    </div>
                    <PercentageBadge value={it.percentage} />
                  </div>

                  <div className="mt-3 flex gap-3 items-start">
                    {it.image_url ? (
                      <button
                        onClick={() => setSelected(it)}
                        className="shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-white/10 hover:border-white/30 transition relative group"
                        data-testid="wia-live-card-thumb"
                      >
                        <img src={it.image_url} alt="verified" className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-[9px] uppercase tracking-widest text-white">
                          Ver PNG
                        </div>
                      </button>
                    ) : (
                      <div className="shrink-0 w-20 h-20 rounded-xl border border-dashed border-white/10 flex items-center justify-center text-[9px] uppercase tracking-widest text-white/25 text-center px-1">
                        Sem PNG
                      </div>
                    )}
                    <p className="text-[12px] text-white/70 leading-snug line-clamp-3 flex-1 min-w-0">
                      {extractSubject(it.message)}
                    </p>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <code className="text-[10px] text-white/40 tracking-wider">{it.short_hash}</code>
                    <button
                      onClick={() => setSelected(it)}
                      className="text-[10px] uppercase tracking-widest text-blue-400/80 hover:text-blue-300"
                      data-testid="wia-live-card-open-modal"
                    >
                      Abrir →
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {!loading && recent.length === 0 && (
            <div className="col-span-full text-center text-white/40 text-sm py-12 border border-white/5 rounded-2xl">
              Nenhuma autenticação registrada ainda.
            </div>
          )}
        </div>
      </div>

      {/* Detailed Table */}
      <div className="container mx-auto px-4 mt-8 mb-12">
        <SectionTitle>Histórico Completo</SectionTitle>
        <div
          className="rounded-2xl overflow-hidden border border-white/[0.06]"
          style={{ background: 'linear-gradient(180deg, rgba(15,18,24,0.7), rgba(8,10,14,0.7))' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="wia-live-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                  <th className="text-left px-4 py-3 font-medium">#</th>
                  <th className="text-left px-4 py-3 font-medium">Foto</th>
                  <th className="text-left px-4 py-3 font-medium">Sócio</th>
                  <th className="text-left px-4 py-3 font-medium">Produto</th>
                  <th className="text-left px-4 py-3 font-medium">Hash</th>
                  <th className="text-right px-4 py-3 font-medium">%</th>
                  <th className="text-right px-4 py-3 font-medium">Canal</th>
                  <th className="text-right px-4 py-3 font-medium">Data</th>
                  <th className="text-right px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={`row-${it.short_hash}-${it.sequence}`} className="border-b border-white/[0.04] hover:bg-white/[0.03] transition">
                    <td className="px-4 py-3 font-mono text-white/50 text-xs">#{String(it.sequence ?? '—').padStart(4, '0')}</td>
                    <td className="px-4 py-3">
                      {it.image_url ? (
                        <button
                          onClick={() => setSelected(it)}
                          className="w-10 h-10 rounded-lg overflow-hidden border border-white/10 hover:border-blue-400/50 transition"
                          data-testid="wia-live-table-thumb"
                        >
                          <img src={it.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ) : (
                        <div className="w-10 h-10 rounded-lg border border-dashed border-white/10 flex items-center justify-center text-[8px] text-white/25">—</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{it.member_name || '—'}</td>
                    <td className="px-4 py-3 text-white/70 max-w-[260px] truncate">{extractSubject(it.message)}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-white/40">{it.short_hash}</td>
                    <td className="px-4 py-3 text-right font-bold" style={{ color: pctColor(it.percentage) }}>
                      {it.percentage ?? '—'}%
                    </td>
                    <td className="px-4 py-3 text-right text-white/50 text-xs uppercase">{it.channel || 'web'}</td>
                    <td className="px-4 py-3 text-right text-white/50 text-xs">{fmtDateTime(it.created_at || it.date)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelected(it)}
                        className="text-blue-400/80 hover:text-blue-300 text-xs"
                        data-testid="wia-live-table-open-modal"
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-white/40 py-8 text-sm">
                      Nenhum registro
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && <VerifyDetailModal item={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>

      {/* Stat detail drawer (bottom sheet) */}
      <AnimatePresence>
        {activeStat && (
          <StatDetailDrawer
            kind={activeStat}
            items={items}
            stats={stats}
            onClose={() => setActiveStat(null)}
            onOpenItem={(it) => { setActiveStat(null); setSelected(it); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ label, value, accent = '#3b82f6', active = false, onClick, testId }) => (
  <motion.button
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0, scale: active ? 1.03 : 1 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    className="relative rounded-2xl p-4 overflow-hidden text-left w-full focus:outline-none transition-colors"
    style={{
      background: 'linear-gradient(135deg, rgba(20,25,35,0.7) 0%, rgba(8,11,18,0.85) 100%)',
      border: `1px solid ${active ? accent : 'rgba(255,255,255,0.08)'}`,
      boxShadow: active ? `0 0 0 1px ${accent}66, 0 0 28px ${accent}33` : 'none',
      cursor: onClick ? 'pointer' : 'default',
    }}
    data-testid={testId}
  >
    <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-30"
         style={{ background: `radial-gradient(circle, ${accent}55 0%, transparent 60%)` }} />
    {active && (
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[8px] font-bold tracking-[0.18em]"
        style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}66` }}
      >
        ATIVO
      </motion.div>
    )}
    <div className="text-[10px] uppercase tracking-widest text-white/40 font-medium pr-12">{label}</div>
    <div className="text-3xl font-['Jost'] font-light mt-2" style={{ color: accent }}>
      {value}
    </div>
  </motion.button>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-semibold mb-3">{children}</h2>
);

const PercentageBadge = ({ value }) => {
  const color = pctColor(value);
  return (
    <div
      className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-bold text-sm"
      style={{
        background: `radial-gradient(circle at 30% 30%, ${color}33, ${color}10 70%, transparent)`,
        border: `1.5px solid ${color}`,
        color,
        boxShadow: `0 0 20px ${color}40`,
      }}
    >
      {value ?? '—'}%
    </div>
  );
};

const VerifyDetailModal = ({ item, onClose }) => {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const verifyUrl =
    item.verify_url ||
    `${window.location.origin}/verify/${item.short_hash}`;

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(verifyUrl, {
      margin: 1,
      width: 220,
      color: { dark: '#0a0d12', light: '#ffffff' },
    })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [verifyUrl]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const downloadPng = () => {
    if (!item.image_url) return;
    const a = document.createElement('a');
    a.href = item.image_url;
    a.download = `wclub-verified-${item.short_hash || 'wia'}.png`;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const color = pctColor(item.percentage);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto overscroll-contain"
      onClick={onClose}
      data-testid="wia-live-modal"
    >
      <motion.div
        initial={{ scale: 0.94, y: 12, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative w-full max-w-3xl rounded-2xl sm:rounded-3xl overflow-hidden my-2 sm:my-6"
        style={{
          background: 'linear-gradient(135deg, rgba(15,18,24,0.98) 0%, rgba(8,10,14,0.98) 100%)',
          border: `1px solid ${color}66`,
          boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 60px ${color}33`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/15 transition"
          data-testid="wia-live-modal-close"
          aria-label="Fechar"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 border-b border-white/5 pr-14">
          <div className="flex items-center gap-3">
            <div
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs"
              style={{ background: `${color}22`, color, border: `1px solid ${color}66` }}
            >
              {item.percentage ?? '—'}%
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-widest text-white/40 font-mono">
                #{String(item.sequence ?? '—').padStart(4, '0')} • {(item.channel || 'web').toUpperCase()}
              </div>
              <div className="text-base sm:text-lg font-['Jost'] font-semibold truncate">{item.member_name || '—'}</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6">
          {/* Verified PNG */}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Foto verificada com selo</div>
            <div
              className="relative rounded-2xl overflow-hidden border max-w-full"
              style={{ borderColor: `${color}40`, background: '#000' }}
            >
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt="Foto verificada"
                  className="w-full h-auto block"
                  data-testid="wia-live-modal-image"
                />
              ) : (
                <div className="aspect-square flex items-center justify-center text-white/40 text-xs text-center px-4">
                  PNG verificada não disponível para esta autenticação legada.
                  <br />
                  Próximas verificações pelo WhatsApp serão arquivadas automaticamente.
                </div>
              )}
            </div>
            {item.image_url && (
              <div className="flex gap-2 mt-3">
                <button
                  onClick={downloadPng}
                  className="flex-1 min-w-0 text-[10px] sm:text-[11px] uppercase tracking-widest text-white/80 hover:text-white border border-white/15 hover:border-white/40 rounded-full py-2 px-2 transition truncate"
                  data-testid="wia-live-modal-download"
                >
                  ↓ Baixar PNG
                </button>
                <a
                  href={item.image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 text-[10px] sm:text-[11px] uppercase tracking-widest text-white/80 hover:text-white border border-white/15 hover:border-white/40 rounded-full py-2 px-2 transition text-center truncate"
                >
                  Abrir
                </a>
              </div>
            )}
          </div>

          {/* QR + Link */}
          <div className="flex flex-col min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">QR Code & Link</div>

            <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR Verify"
                  className="w-40 h-40 sm:w-44 sm:h-44 rounded-lg bg-white p-2"
                  data-testid="wia-live-modal-qr"
                />
              ) : (
                <div className="w-40 h-40 sm:w-44 sm:h-44 rounded-lg bg-white/10 animate-pulse" />
              )}
              <code className="mt-3 text-[10px] text-white/40 tracking-wider break-all text-center">{item.short_hash}</code>
            </div>

            <div className="mt-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 flex items-center gap-2 min-w-0">
              <span className="text-[11px] text-white/60 truncate flex-1 min-w-0" data-testid="wia-live-modal-url">
                {verifyUrl}
              </span>
              <button
                onClick={copyLink}
                className="shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/15 hover:border-white/40 text-white/70 hover:text-white transition"
                data-testid="wia-live-modal-copy"
              >
                {copied ? '✓' : 'Copiar'}
              </button>
            </div>

            <a
              href={verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 text-[11px] uppercase tracking-widest text-blue-400/80 hover:text-blue-300 text-center py-2 rounded-full border border-blue-400/20 hover:border-blue-400/50 transition"
              data-testid="wia-live-modal-open-verify"
            >
              Abrir página de verificação →
            </a>

            <div className="mt-4 text-[11px] text-white/50 leading-relaxed line-clamp-6 break-words">
              {item.message || '—'}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default WIALive;

// ============ STAT DETAIL DRAWER ============
const fmtDay = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch { return ''; }
};
const dayKey = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
};
const isToday = (iso) => {
  if (!iso) return false;
  const a = new Date(iso);
  const b = new Date();
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const StatDetailDrawer = ({ kind, items, stats, onClose, onOpenItem }) => {
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const meta = {
    total: { title: 'Total de Autenticações', subtitle: 'Volume diário nos últimos 30 dias', accent: '#3b82f6' },
    today: { title: 'Hoje', subtitle: 'Linha do tempo das autenticações de hoje', accent: '#22d3ee' },
    avg: { title: 'Média Hoje', subtitle: 'Distribuição de autenticidade (histograma)', accent: pctColor(stats.avg_today) },
    members: { title: 'Sócios Hoje', subtitle: 'Sócios únicos com autenticações hoje', accent: '#a3e635' },
  }[kind] || { title: '', subtitle: '', accent: '#3b82f6' };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[105] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="wia-live-stat-drawer"
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(15,18,24,0.99) 0%, rgba(8,10,14,0.99) 100%)',
          border: `1px solid ${meta.accent}44`,
          boxShadow: `0 -20px 60px rgba(0,0,0,0.6), 0 0 60px ${meta.accent}22`,
          maxHeight: '90vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="px-5 sm:px-6 pt-4 sm:pt-6 pb-3 flex items-start justify-between gap-3 border-b border-white/5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.accent, boxShadow: `0 0 8px ${meta.accent}` }} />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">{meta.subtitle}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-['Jost'] font-light tracking-wide" style={{ color: meta.accent }}>
              {meta.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white bg-black/40 hover:bg-black/60 border border-white/10"
            data-testid="wia-live-stat-drawer-close"
            aria-label="Fechar"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 pb-6 pt-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
          {kind === 'total' && <Stat30DaysChart items={items} accent={meta.accent} />}
          {kind === 'today' && <StatTodayTimeline items={items} accent={meta.accent} onOpenItem={onOpenItem} />}
          {kind === 'avg' && <StatHistogram items={items} stats={stats} />}
          {kind === 'members' && <StatMembersToday items={items} accent={meta.accent} onOpenItem={onOpenItem} />}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// ---- 1. Bar chart: last 30 days ----
const Stat30DaysChart = ({ items, accent }) => {
  const data = useMemo(() => {
    const map = new Map();
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      map.set(k, { day: k, label: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }), count: 0 });
    }
    (items || []).forEach((it) => {
      const k = dayKey(it.created_at || it.date);
      if (map.has(k)) map.get(k).count += 1;
    });
    return Array.from(map.values());
  }, [items]);

  const total = data.reduce((s, d) => s + d.count, 0);
  const max = Math.max(...data.map((d) => d.count), 1);
  const peak = data.find((d) => d.count === max && max > 0);
  const [hoverIdx, setHoverIdx] = useState(null);

  // SVG dims
  const W = 600, H = 220;
  const padL = 26, padR = 8, padT = 12, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barGap = 4;
  const barW = Math.max(2, (innerW - barGap * (data.length - 1)) / data.length);

  return (
    <div data-testid="wia-live-30d-chart">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MiniStat label="Período" value="30 dias" accent={accent} />
        <MiniStat label="Total" value={total} accent={accent} />
        <MiniStat label="Pico" value={peak ? `${max} • ${peak.label}` : '—'} accent={accent} />
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-56 overflow-visible">
          {/* baseline */}
          <line x1={padL} x2={W - padR} y1={H - padB + 0.5} y2={H - padB + 0.5} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          {data.map((d, i) => {
            const x = padL + i * (barW + barGap);
            const h = (d.count / max) * innerH;
            const y = H - padB - h;
            const isPeak = d.count === max && max > 0;
            const isHover = hoverIdx === i;
            return (
              <g key={d.day} onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}>
                {/* invisible hit-area */}
                <rect x={x - 1} y={padT} width={barW + 2} height={innerH} fill="transparent" />
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(d.count > 0 ? 2 : 0, h)}
                  rx="2"
                  fill={accent}
                  opacity={isHover ? 1 : (isPeak ? 1 : 0.55)}
                  style={{ transition: 'opacity 0.18s ease' }}
                />
                {isPeak && (
                  <rect x={x} y={y - 4} width={barW} height={2} rx="1" fill="#fff" opacity="0.7" />
                )}
              </g>
            );
          })}
          {/* X-axis sparse labels: every 4th */}
          {data.map((d, i) => {
            if (i % 4 !== 0 && i !== data.length - 1) return null;
            const x = padL + i * (barW + barGap) + barW / 2;
            return (
              <text key={`lbl-${d.day}`} x={x} y={H - 10} textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.4)" fontFamily="ui-monospace, SFMono-Regular, monospace">
                {d.label}
              </text>
            );
          })}
          {/* Y-axis ticks: 0, mid, max */}
          {[0, Math.ceil(max / 2), max].map((v, i) => {
            const y = H - padB - (v / max) * innerH;
            return (
              <g key={`y-${i}`}>
                <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9" fill="rgba(255,255,255,0.3)">{v}</text>
              </g>
            );
          })}
        </svg>
        {/* Hover tooltip */}
        {hoverIdx !== null && (
          <div
            className="absolute pointer-events-none rounded-lg px-2.5 py-1.5 text-[10px] font-mono"
            style={{
              left: `${((padL + hoverIdx * (barW + barGap) + barW / 2) / W) * 100}%`,
              top: 0,
              transform: 'translate(-50%, -8px)',
              background: 'rgba(8,11,18,0.95)',
              border: `1px solid ${accent}66`,
              color: '#fff',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ color: accent }}>{data[hoverIdx].count}</span> em {data[hoverIdx].label}
          </div>
        )}
      </div>
    </div>
  );
};

// ---- 2. Today timeline ----
const StatTodayTimeline = ({ items, accent, onOpenItem }) => {
  const today = useMemo(
    () => (items || []).filter((it) => isToday(it.created_at || it.date))
      .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date)),
    [items]
  );

  if (today.length === 0) {
    return <EmptyState label="Nenhuma autenticação hoje ainda" />;
  }

  return (
    <div data-testid="wia-live-today-timeline" className="relative">
      <div className="absolute left-[18px] top-2 bottom-2 w-px bg-white/10" />
      <div className="space-y-3">
        {today.map((it) => {
          const c = pctColor(it.percentage);
          return (
            <button
              key={`${it.short_hash}-${it.sequence}`}
              onClick={() => onOpenItem && onOpenItem(it)}
              className="relative w-full flex items-start gap-3 text-left rounded-xl p-3 border border-white/[0.04] hover:border-white/15 hover:bg-white/[0.03] transition"
              data-testid="wia-live-today-item"
            >
              <div
                className="shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold relative z-10"
                style={{ background: `${c}22`, color: c, border: `1.5px solid ${c}` }}
              >
                {it.percentage ?? '—'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[10px] tracking-widest text-white/40 font-mono uppercase">
                  <span>#{String(it.sequence ?? '—').padStart(4, '0')}</span>
                  <span>•</span>
                  <span>{(it.channel || 'web').toUpperCase()}</span>
                  <span className="ml-auto text-white/50">
                    {new Date(it.created_at || it.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="text-sm font-semibold truncate mt-0.5">{it.member_name || '—'}</div>
                <div className="text-[11px] text-white/60 line-clamp-2 mt-0.5">{extractSubject(it.message)}</div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-3 text-center text-[10px] uppercase tracking-widest text-white/30">
        {today.length} autenticação{today.length !== 1 ? 'ões' : ''} hoje
      </div>
    </div>
  );
};

// ---- 3. Histogram of today's % ----
const StatHistogram = ({ items, stats }) => {
  const today = useMemo(() => (items || []).filter((it) => isToday(it.created_at || it.date)), [items]);

  const bins = useMemo(() => {
    const buckets = [
      { range: '0–49', min: 0, max: 49, color: '#ef4444' },
      { range: '50–69', min: 50, max: 69, color: '#f59e0b' },
      { range: '70–79', min: 70, max: 79, color: '#a3e635' },
      { range: '80–89', min: 80, max: 89, color: '#22d3ee' },
      { range: '90–100', min: 90, max: 100, color: '#3b82f6' },
    ].map((b) => ({
      ...b,
      count: today.filter((it) => (it.percentage ?? -1) >= b.min && (it.percentage ?? -1) <= b.max).length,
    }));
    return buckets;
  }, [today]);

  if (today.length === 0) {
    return <EmptyState label="Nenhuma autenticação hoje pra distribuir" />;
  }

  const maxCount = Math.max(...bins.map((b) => b.count), 1);
  const W = 600, H = 200;
  const padL = 18, padR = 8, padT = 22, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barGap = 12;
  const barW = (innerW - barGap * (bins.length - 1)) / bins.length;

  return (
    <div data-testid="wia-live-histogram">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MiniStat label="Média" value={`${stats.avg_today ?? 0}%`} accent={pctColor(stats.avg_today)} />
        <MiniStat label="Máxima" value={`${stats.max_today ?? 0}%`} accent={pctColor(stats.max_today)} />
        <MiniStat label="Mínima" value={`${stats.min_today ?? 0}%`} accent={pctColor(stats.min_today)} />
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-48">
          <line x1={padL} x2={W - padR} y1={H - padB + 0.5} y2={H - padB + 0.5} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          {bins.map((b, i) => {
            const x = padL + i * (barW + barGap);
            const h = (b.count / maxCount) * innerH;
            const y = H - padB - h;
            return (
              <g key={b.range}>
                {b.count > 0 ? (
                  <rect x={x} y={y} width={barW} height={Math.max(4, h)} rx="6" fill={b.color} opacity={0.92} />
                ) : (
                  <rect x={x} y={H - padB - 4} width={barW} height={4} rx="2" fill={b.color} opacity={0.2} />
                )}
                <text x={x + barW / 2} y={H - 10} textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.55)" fontFamily="ui-monospace, SFMono-Regular, monospace">
                  {b.range}
                </text>
                {b.count > 0 && (
                  <text x={x + barW / 2} y={y - 6} textAnchor="middle" fontSize="13" fontWeight="700" fill={b.color}>
                    {b.count}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="mt-3 grid grid-cols-5 gap-1.5">
        {bins.map((b) => (
          <div key={b.range} className="rounded-lg border border-white/5 px-2 py-1.5 text-center" style={{ background: `${b.color}10` }}>
            <div className="text-[8px] uppercase tracking-widest text-white/40">{b.range}%</div>
            <div className="text-sm font-bold" style={{ color: b.color }}>{b.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- 4. Members today list ----
const StatMembersToday = ({ items, accent, onOpenItem }) => {
  const todayItems = useMemo(() => (items || []).filter((it) => isToday(it.created_at || it.date)), [items]);

  const members = useMemo(() => {
    const m = new Map();
    todayItems.forEach((it) => {
      const k = it.member_id || it.member_name;
      const e = m.get(k) || {
        id: it.member_id,
        name: it.member_name || '—',
        count: 0,
        avg: 0,
        last: null,
        items: [],
      };
      e.count += 1;
      e.items.push(it);
      const ts = new Date(it.created_at || it.date).getTime();
      if (!e.last || ts > new Date(e.last.created_at || e.last.date).getTime()) e.last = it;
      m.set(k, e);
    });
    return Array.from(m.values()).map((e) => ({
      ...e,
      avg: Math.round(e.items.reduce((s, x) => s + (x.percentage || 0), 0) / e.items.length),
    })).sort((a, b) => b.count - a.count);
  }, [todayItems]);

  if (members.length === 0) {
    return <EmptyState label="Nenhum sócio autenticou produto hoje ainda" />;
  }

  const initial = (n) => (n || '?').trim().charAt(0).toUpperCase();

  return (
    <div data-testid="wia-live-members-today" className="space-y-2">
      {members.map((m) => {
        const c = pctColor(m.avg);
        return (
          <button
            key={m.id || m.name}
            onClick={() => m.last && onOpenItem && onOpenItem(m.last)}
            className="w-full flex items-center gap-3 rounded-xl p-3 border border-white/[0.04] hover:border-white/15 hover:bg-white/[0.03] transition text-left"
            data-testid="wia-live-member-row"
          >
            <div
              className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center font-['Jost'] font-bold"
              style={{
                background: `linear-gradient(135deg, ${accent}33, ${accent}10)`,
                color: accent,
                border: `1px solid ${accent}55`,
              }}
            >
              {initial(m.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{m.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">
                {m.count} autenticaç{m.count !== 1 ? 'ões' : 'ão'} • média <span style={{ color: c }}>{m.avg}%</span>
              </div>
            </div>
            <div className="shrink-0 text-[10px] uppercase tracking-widest text-white/40">
              Ver último →
            </div>
          </button>
        );
      })}
    </div>
  );
};

const MiniStat = ({ label, value, accent }) => (
  <div className="rounded-xl border border-white/5 px-3 py-2" style={{ background: `${accent}08` }}>
    <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
    <div className="text-base font-['Jost'] font-semibold mt-0.5" style={{ color: accent }}>{value}</div>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="text-center py-10 text-white/40 text-sm" data-testid="wia-live-empty">{label}</div>
);